import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  Signal,
  viewChildren
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { ButtonComponent, IconComponent, SoftSlotDirective, TranslatePipe, translator } from '@softline/ui-core';
import { PageFilterService } from '../../services/page-filter.service';
import { PageFilterItem } from '../../types/page-filter-item';
import { PageFilterRef } from '../../types/page-filter.ref';
import { Filter } from '../../../types/filter';
import { FilterOperator } from '../../../types/filter-operators';
import { FilterInputBase } from '../filter-inputs/filter-input.base';
import { toQueryFilter } from '../../../helper/to-query-filter.function';
import { excludeFilterNode } from '../../../helper/exclude-filter-node.function';
import { formatFilterValueSummary, summarizeFilterValue } from '../../../helper/format-filter-value.function';
import { PageFilterDropdownComponent } from '../page-filter-dropdown/page-filter-dropdown.component';
import { PageFilterSelectItemComponent } from '../page-filter-select-item/page-filter-select-item.component';
import { PageFilterDateRangeItemComponent } from '../page-filter-date-range-item/page-filter-date-range-item.component';
import { PageFilterDateRangeReferenceItemComponent } from '../page-filter-date-range-reference-item/page-filter-date-range-reference-item.component';
import { resolveFilterInputComponent } from '../../helper/resolve-filter-input.function';

/** The pinned filter items of the bar: a design-system multi select per in/notIn item, a
    generic dropdown editor for every other operator, a button per toggle. */
@Component({
  selector: 'soft-page-filter-menu-bar',
  imports: [
    NgComponentOutlet,
    ButtonComponent,
    IconComponent,
    SoftSlotDirective,
    TranslatePipe,
    PageFilterDropdownComponent,
    PageFilterSelectItemComponent,
    PageFilterDateRangeItemComponent,
    PageFilterDateRangeReferenceItemComponent,
  ],
  templateUrl: './page-filter-menu-bar.component.html',
  styleUrl: './page-filter-menu-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterMenuBarComponent {
  private readonly t = translator();

  readonly config = input.required<PageFilterRef>();

  readonly service = computed<PageFilterService>(() => this.config().service);

  private readonly dropdowns = viewChildren(PageFilterDropdownComponent);
  private readonly selectItems = viewChildren(PageFilterSelectItemComponent);
  private readonly dateRangeItems = viewChildren(PageFilterDateRangeItemComponent);
  private readonly dateRangeReferenceItems = viewChildren(PageFilterDateRangeReferenceItemComponent);
  private readonly outlets = viewChildren(NgComponentOutlet);

  // pinned items only - unpinned values show as chips in the row below
  readonly visibleItems = computed(() =>
    this.service().items.filter((item) => (!item.visible || item.visible()) && !!item.menuBar)
  );

  constructor() {
    // the sidebar can be opened from outside the bar (page button, ref.open()) - an open
    // dropdown would otherwise float over it
    effect(() => {
      if (this.service().opened()) this.closeDropdowns();
    });
  }

  /** in/notIn items are lists → design-system multi select; everything else gets the generic editor */
  isListItem(item: PageFilterItem<any, any>): boolean {
    return item.operator === 'in' || item.operator === 'notIn';
  }

  /** the value part of a pinned dropdown's label: formatted single value, count or range */
  itemValue(item: PageFilterItem<any, any>): string | null {
    return formatFilterValueSummary(
      summarizeFilterValue(item.config, this.service().getSourceItemValue(item)),
      this.t
    );
  }

  itemComponent(item: PageFilterItem<any, any>) {
    return resolveFilterInputComponent(item, this.itemOperator(item));
  }

  // itemInputs() feeds *ngComponentOutlet's [inputs], which NgComponentOutlet re-pushes via
  // setInput() on every CD cycle. A plain object literal here would be a new reference on
  // every call (e.g. one per checkbox click or scroll event inside the mounted picker), which
  // resource() treats as changed params and re-fires the loader. Cache one computed() per item
  // so unchanged content keeps returning the same reference.
  private readonly itemInputsCache = new Map<PageFilterItem<any, any>, Signal<Record<string, unknown>>>();

  itemInputs(item: PageFilterItem<any, any>): Record<string, unknown> {
    let cached = this.itemInputsCache.get(item);
    if (!cached) {
      cached = computed(
        () => ({
          item: item.config,
          itemFilter: this.service().getSourceItemValue(item),
          operator: this.itemOperator(item),
          dataService: item.source.dataService,
          queryFilter: this.itemQueryFilter(item),
        }),
        {
          // item/dataService are stable references and operator is a primitive - comparing
          // those structurally would be wrong anyway; dataService in particular holds RxJS
          // Subscription internals with circular refs that crash JSON.stringify.
          equal: (a, b) =>
            a.item === b.item &&
            a.dataService === b.dataService &&
            a.operator === b.operator &&
            JSON.stringify(a.itemFilter) === JSON.stringify(b.itemFilter) &&
            JSON.stringify(a.queryFilter) === JSON.stringify(b.queryFilter),
        }
      );
      this.itemInputsCache.set(item, cached);
    }
    return cached();
  }

  private itemOperator(item: PageFilterItem<any, any>): FilterOperator {
    const value = this.service().getSourceItemValue(item);
    return (value as { operator?: FilterOperator } | null)?.operator ?? item.operator;
  }

  private itemQueryFilter(item: PageFilterItem<any, any>) {
    const state = item.source.state;
    const value = this.service().getSourceItemValue(item);
    const tree = value ? excludeFilterNode(state.state(), value.uuid) : state.state();
    return tree ? toQueryFilter(tree, state.config) : null;
  }

  /** Übernehmen: commit and close; the overlay hands focus back to the trigger. */
  applyItem(item: PageFilterItem<any, any>, dropdown: PageFilterDropdownComponent): void {
    // only the open dropdown mounts an editor, so the first outlet is the one being applied
    const outlet = this.outlets()[0] ?? null;
    const filterInput =
      (outlet?.componentInstance as FilterInputBase<any, FilterOperator> | null) ?? null;
    if (!filterInput) return;
    const filter: Filter<any> | null = filterInput.getFilterValue();
    this.service().applyItemFilter(item, filter, 'source');
    dropdown.close();
  }

  isToggleActive(item: PageFilterItem<any, any>): boolean {
    return this.service().isToggleItemActive(item, 'source');
  }

  // toggles commit immediately, like the item dropdowns
  toggleItem(item: PageFilterItem<any, any>): void {
    this.service().toggleItemFilter(item, 'source');
  }

  closeDropdowns(): void {
    for (const dropdown of this.dropdowns()) dropdown.close();
    for (const selectItem of this.selectItems()) selectItem.close();
    for (const dateItem of this.dateRangeItems()) dateItem.close();
    for (const referenceItem of this.dateRangeReferenceItems()) referenceItem.close();
  }
}
